import { FC, RefObject, useLayoutEffect, useState } from 'react';
import { Button, Dropdown, DropdownChangeEvent, InputText, Sidebar, useGlobalToast } from '@repo/atomic-components';
import { classNames } from 'primereact/utils';
import { useLazyPostUserAddQuery, useLazyPostUserEditQuery, UserEditProps } from '@/api';
import {
  CreateEditUserSidebarFormData,
  CreateEditUserSidebarFormDataFieldsName,
  useCreateEditCaseForm,
  userRoleTypeOptionsForCreate,
  userRoleTypeOptionsForEdit,
} from '../hooks';
import { selectActiveUsersList, selectUserEditId, setUserEditId, useAppDispatch, useAppSelector } from '@/store';
import { deepEqual } from '@/utils';
import { NAV_HEIGHT_PX, SIDEBAR_PANEL_SHADOW } from '@/constants';
import { GreenButton } from '@/ui/components/GreenButton';
import { OutlineButton } from '@/ui/components/OutlineButton';
import { themedDropdownPt } from '@/ui/components/themedDropdownPt';

// Letters + hyphens only. Spaces are explicitly rejected because the
// UserAvatar's initials helper splits on whitespace and would otherwise
// pick the first/last *space-separated* token (so "Langley McNamara"
// surnaming "Scott" would show "SM" instead of "SL"). The input handler
// substitutes a hyphen for any space the user types, keeping double-
// barrel names whitespace-free at the source.
const allowedCaseReferenceInputSymbolsRegex = /^[a-zA-Z-]*$/;

interface CreateEditUserSidebarProps {
  closeSidebar: () => void;
}

export const CreateEditUserSidebar: FC<CreateEditUserSidebarProps> = ({ closeSidebar }) => {
  const dispatch = useAppDispatch();
  const globalToastRef = useGlobalToast();
  const [triggerPostUserAdd] = useLazyPostUserAddQuery();

  const { formData, setFormData, handleValueChange, canSubmit } = useCreateEditCaseForm();

  const activeUsersList = useAppSelector(selectActiveUsersList);
  const userEditId = useAppSelector(selectUserEditId);

  const [initEditFormData, setInitEditFormData] = useState<CreateEditUserSidebarFormData | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [triggerPostUserEdit] = useLazyPostUserEditQuery();

  useLayoutEffect(() => {
    if (userEditId) {
      const activeUser = activeUsersList.find((item) => item.userId === userEditId);

      if (activeUser) {
        const selectedRoleType =
          userRoleTypeOptionsForEdit.find(
            (option) => option.code === activeUser.userRole || option.name === activeUser.userRole,
          ) || userRoleTypeOptionsForEdit[0];

        const editFormData = {
          [CreateEditUserSidebarFormDataFieldsName.FirstName]: activeUser.userFirstName,
          [CreateEditUserSidebarFormDataFieldsName.LastName]: activeUser.userLastName,
          [CreateEditUserSidebarFormDataFieldsName.EmailAddress]: activeUser.userEmail,
          [CreateEditUserSidebarFormDataFieldsName.UserRole]: selectedRoleType,
        };

        setInitEditFormData(editFormData);
        setFormData(editFormData);
        setIsEditMode(true);
      }
    }
  }, [activeUsersList, setFormData, userEditId]);

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.replace(/\s+/g, '-');
    const isValid = allowedCaseReferenceInputSymbolsRegex.test(inputValue);

    if (isValid) {
      handleValueChange(CreateEditUserSidebarFormDataFieldsName.FirstName, inputValue);
    }
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.replace(/\s+/g, '-');
    const isValid = allowedCaseReferenceInputSymbolsRegex.test(inputValue);

    if (isValid) {
      handleValueChange(CreateEditUserSidebarFormDataFieldsName.LastName, inputValue);
    }
  };

  const handleEmailAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    handleValueChange(CreateEditUserSidebarFormDataFieldsName.EmailAddress, inputValue);
  };

  const handleCreateClick = (e: React.MouseEvent<HTMLButtonElement>, hide: (event: React.SyntheticEvent) => void) => {
    const firstName = formData[CreateEditUserSidebarFormDataFieldsName.FirstName];
    const lastName = formData[CreateEditUserSidebarFormDataFieldsName.LastName];
    const emailAddress = formData[CreateEditUserSidebarFormDataFieldsName.EmailAddress];
    const userRole = formData[CreateEditUserSidebarFormDataFieldsName.UserRole]?.code;
    if (firstName && lastName && emailAddress && userRole) {
      setIsSubmitting(true);
      triggerPostUserAdd({
        firstName: firstName,
        lastName: lastName,
        emailAddress: emailAddress,
        userRole: userRole,
      })
        .unwrap()
        .then(() => {
          dispatch(setUserEditId(null));
          hide(e);
        })
        .catch(() => {
          globalToastRef.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Creating operation failed',
          });
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  const handleEditClick = (e: React.MouseEvent<HTMLButtonElement>, hide: (event: React.SyntheticEvent) => void) => {
    if (!initEditFormData || !userEditId) return;

    // Prepare changed fields only (except userEditId)
    const changed: Partial<UserEditProps> = {};
    // First Name
    if (
      !deepEqual(
        formData[CreateEditUserSidebarFormDataFieldsName.FirstName],
        initEditFormData[CreateEditUserSidebarFormDataFieldsName.FirstName],
      )
    ) {
      changed.firstName = formData[CreateEditUserSidebarFormDataFieldsName.FirstName];
    }
    // Last Name
    if (
      !deepEqual(
        formData[CreateEditUserSidebarFormDataFieldsName.LastName],
        initEditFormData[CreateEditUserSidebarFormDataFieldsName.LastName],
      )
    ) {
      changed.lastName = formData[CreateEditUserSidebarFormDataFieldsName.LastName];
    }
    // Email Address
    if (
      !deepEqual(
        formData[CreateEditUserSidebarFormDataFieldsName.EmailAddress],
        initEditFormData[CreateEditUserSidebarFormDataFieldsName.EmailAddress],
      )
    ) {
      changed.emailAddress = formData[CreateEditUserSidebarFormDataFieldsName.EmailAddress];
    }
    // User Role
    if (
      !deepEqual(
        formData[CreateEditUserSidebarFormDataFieldsName.UserRole]?.code,
        initEditFormData[CreateEditUserSidebarFormDataFieldsName.UserRole]?.code,
      )
    ) {
      changed.role = formData[CreateEditUserSidebarFormDataFieldsName.UserRole]?.code;
    }

    // Always send caseId
    const payload: UserEditProps = {
      userToEditId: userEditId,
      ...changed,
    };

    // If only caseId is present, do not call API
    const changedKeys = Object.keys(changed);

    if (changedKeys.length === 0) return;
    setIsSubmitting(true);
    triggerPostUserEdit(payload)
      .unwrap()
      .then(() => {
        dispatch(setUserEditId(null));
        hide(e);
      })
      .catch(() => {
        globalToastRef.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Editing operation failed',
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <Sidebar
      visible
      closeOnEscape={false}
      dismissable={false}
      position="right"
      onHide={closeSidebar}
      // PrimeReact writes the mask z-index inline via ZIndexUtils; the
      // `!z-[1000]` override beats that. Offset the mask below the 48px
      // nav so the dimming layer doesn't cover the header — the panel
      // inherits the mask's height.
      pt={{
        mask: {
          className: '!z-[1000]',
          style: { top: NAV_HEIGHT_PX, height: `calc(100% - ${NAV_HEIGHT_PX}px)` },
        },
      }}
      className={classNames(
        '!border-fletchers-gray-110/55 !w-[412px] max-w-[412px] min-w-[412px] !border-l',
        SIDEBAR_PANEL_SHADOW,
      )}
      content={({ closeIconRef, hide }) => (
        <div className="flex h-full flex-col pr-0 pb-4 pl-6">
          <div className="flex flex-1 flex-col gap-6 overflow-y-auto pt-6 pr-6">
            <div className="flex flex-shrink-0 text-[20px]/[28px] font-[600]">
              {isEditMode ? 'Edit user' : 'Create user'}
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="first-name" className="!text-fletchers-gray-200 block text-[14px]/[20px] font-[500]">
                First name
              </label>
              <InputText
                id="first-name"
                aria-describedby="First name"
                value={formData[CreateEditUserSidebarFormDataFieldsName.FirstName]}
                onChange={handleFirstNameChange}
                className="border-fletchers-gray-110! hover:border-fletchers-gray-110! focus:border-fletchers-gray-110! h-8! text-[14px]/[20px]! focus:outline-none!"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="last-name" className="!text-fletchers-gray-200 block text-[14px]/[20px] font-[500]">
                Last name
              </label>
              <InputText
                id="last-name"
                aria-describedby="Last name"
                value={formData[CreateEditUserSidebarFormDataFieldsName.LastName]}
                onChange={handleLastNameChange}
                className="border-fletchers-gray-110! hover:border-fletchers-gray-110! focus:border-fletchers-gray-110! h-8! text-[14px]/[20px]! focus:outline-none!"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="email-address" className="!text-fletchers-gray-200 block text-[14px]/[20px] font-[500]">
                Email address
              </label>
              <InputText
                id="email-address"
                aria-describedby="Email address"
                value={formData[CreateEditUserSidebarFormDataFieldsName.EmailAddress]}
                onChange={handleEmailAddressChange}
                disabled={isEditMode}
                className="border-fletchers-gray-110! hover:border-fletchers-gray-110! focus:border-fletchers-gray-110! h-8! text-[14px]/[20px]! focus:outline-none!"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="select-role" className="!text-fletchers-gray-200 block text-[14px]/[20px] font-[500]">
                Select role
              </label>
              <Dropdown
                id="select-role"
                options={isEditMode ? userRoleTypeOptionsForEdit : userRoleTypeOptionsForCreate}
                value={formData[CreateEditUserSidebarFormDataFieldsName.UserRole]}
                onChange={(event: DropdownChangeEvent) =>
                  handleValueChange(CreateEditUserSidebarFormDataFieldsName.UserRole, event.value)
                }
                dataKey="code"
                optionLabel="name"
                {...themedDropdownPt()}
              />
            </div>
          </div>
          <div className="border-fletchers-gray-110 mt-auto mr-6 flex flex-row items-center justify-end gap-2 border-t pt-4">
            <OutlineButton
              ref={closeIconRef as RefObject<Button | null>}
              onClick={(e) => {
                dispatch(setUserEditId(null));
                hide(e);
              }}
            >
              Cancel
            </OutlineButton>
            <GreenButton
              disabled={!canSubmit || isSubmitting}
              loading={isSubmitting}
              onClick={(e) => (isEditMode ? handleEditClick(e, hide) : handleCreateClick(e, hide))}
            >
              Confirm
            </GreenButton>
          </div>
        </div>
      )}
    />
  );
};
